import { Box, Stack } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";

import FormStepperHeader from "./FormStepperHeader";
import FormStepperActions from "./FormStepperActions";
import { genericMemo } from "../../../../utils/utils";
import { IFormStepperProps } from "./models";
import NotAvaiableModal from "./NotAvaiableModal";

const FormStepper = <T extends FieldValues>({
  steps,
  finishLabel,
  onFinish,
  initialStep,
  activeStep: controlledActiveStep,
  onActiveStepChange,
  canGoBack,
  error,
  onBeforeNext,
  onBeforeValidate,
}: IFormStepperProps<T>) => {
  const { trigger } = useFormContext<T>();

  const [open, setOpen] = useState(false);

  const [internalActiveStep, setInternalActiveStep] = React.useState(
    initialStep || 0
  );

  const activeStep = controlledActiveStep ?? internalActiveStep;

  const setActiveStep = useCallback(
    (step: number | ((previousStep: number) => number)) => {
      const nextStep = typeof step === "function" ? step(activeStep) : step;
      setInternalActiveStep(nextStep);
      onActiveStepChange?.(nextStep);
    },
    [activeStep, onActiveStepChange]
  );

  const handleNext = useCallback(async () => {
    if (onBeforeValidate && !(await onBeforeValidate(activeStep))) return;

    const valid = await trigger(steps[activeStep].fields);
    if (valid) {
      const canContinue = onBeforeNext
        ? await onBeforeNext(activeStep)
        : true;
      if (!canContinue) return;

      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }, [trigger, activeStep, steps, onBeforeNext, onBeforeValidate]);

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (error && error.errors.GetCalendarConsultationAvailable) {
      setActiveStep(3);
      setOpen(true);
    }
  }, [error]);

  return (
    <Stack
      sx={{
        height: "100%",
        paddingInline: 1,
        gap: 2,
        width: "100%",
        overflow: "auto",
      }}
    >
      <NotAvaiableModal onClose={() => setOpen(false)} open={open} />
      <FormStepperHeader<T> activeStep={activeStep} steps={steps} />
      <Box width={"100%"} overflow={"auto"} sx={{ paddingInline: 2 }}>
        {steps[activeStep].element}
      </Box>
      <FormStepperActions
        finishLabel={finishLabel}
        activeStep={activeStep}
        canGoBack={canGoBack}
        isLastStep={activeStep === steps.length - 1}
        onBack={handleBack}
        onNext={handleNext}
        onFinish={onFinish}
      />
    </Stack>
  );
};

export default genericMemo(FormStepper);
